// src/components/GoToPremiumDesktop.jsx
import { useContext, useEffect, useMemo, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import LeftMenu from "./menus/LeftMenu";
import HeaderBar from "./headers/HeaderBar";
import { useAuth } from "../context/AuthContext";
import { LanguageContext } from "../context/LanguageContext";
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import SecurityIcon from "@mui/icons-material/Security";
import TimelineIcon from "@mui/icons-material/Timeline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// Stripe payments SDK
import { getApp } from "firebase/app";
import {
  getStripePayments,
  getCurrentUserSubscriptions,
  createCheckoutSession,
  getProducts,
} from "@invertase/firestore-stripe-payments";

const app = getApp();
const payments = getStripePayments(app, {
  productsCollection: "products",
  customersCollection: "customers",
});

const heroIcons = [
  AutoAwesomeIcon,
  RocketLaunchIcon,
  QueryStatsIcon,
  SupportAgentIcon,
  SecurityIcon,
  TimelineIcon,
];

const priceId = "price_1ShRghKxPduZsYMS7HitXy40";
const salesEmail = "arnaud@5ideasproduction.com";

export default function GoToPremiumDesktop() {
  const theme = useTheme();
  const { translate } = useContext(LanguageContext);
  const { currentUser } = useAuth();

  const [role, setRole] = useState("Normal User");
  const [subscriptions, setSubscriptions] = useState([]);
  const [subsLoading, setSubsLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const isEuVisitor = useMemo(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
      if (tz.startsWith("Europe/")) return true;
    } catch {}
    const lang = (navigator?.language || "").toLowerCase();
    return (
      lang.startsWith("fr") ||
      lang.startsWith("de") ||
      lang.startsWith("es") ||
      lang.startsWith("it") ||
      lang.startsWith("pt") ||
      lang.startsWith("nl") ||
      lang.startsWith("pl") ||
      lang.startsWith("sv") ||
      lang.startsWith("da") ||
      lang.startsWith("fi")
    );
  }, []);
  const currencySymbol = isEuVisitor ? "€" : "$";
  const premiumPrice = isEuVisitor ? 17 : 19.9;
  const formatPrice = (v) => (Number.isInteger(v) ? v : v.toFixed(1));

  // Fetch Stripe products once to make sure checkout is configured correctly
  useEffect(() => {
    getProducts(payments, { includePrices: true, activeOnly: true })
      .then((prods) => {
        console.log("[Premium][Desktop] products loaded", {
          count: prods?.length,
          ids: prods?.map((p) => p.id),
          prices: prods?.flatMap((p) => p.prices?.map((pr) => pr.id)),
        });
      })
      .catch((err) =>
        console.error("[Premium][Desktop] getProducts error", err)
      );
  }, []);

  // Fetch role for header context
  useEffect(() => {
    if (!currentUser) return;
    getDoc(doc(db, "profilsUsers", currentUser.uid))
      .then(
        (snap) => snap.exists() && setRole(snap.data().role || "Normal User")
      )
      .catch(() => setRole("Normal User"));
  }, [currentUser]);

  // Load active subscription to disable CTA when already upgraded
  useEffect(() => {
    let cancelled = false;
    if (!currentUser) {
      setSubscriptions([]);
      setSubsLoading(false);
      return () => {};
    }

    const fetchSubs = async () => {
      try {
        const subs = await getCurrentUserSubscriptions(payments, {
          status: "active",
        });
        if (!cancelled) setSubscriptions(subs);
      } catch (err) {
        console.error("Failed to load subscriptions", err);
        if (!cancelled) setSubscriptions([]);
      } finally {
        if (!cancelled) setSubsLoading(false);
      }
    };

    fetchSubs();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  const handleSubscribe = async () => {
    console.log("[Premium][Desktop] checkout start", {
      priceId,
      hasActive,
      subsLoading,
      checkoutLoading,
    });
    setCheckoutLoading(true);
    try {
      const session = await createCheckoutSession(payments, {
        price: priceId,
        success_url: `${window.location.origin}/app/premium/success`,
        cancel_url: `${window.location.origin}/app/premium/cancel`,
      });
      console.log("[Premium][Desktop] session created", session);
      if (session?.url) {
        window.location.assign(session.url);
      } else {
        console.error("[Premium][Desktop] missing session url", session);
        setCheckoutLoading(false);
      }
    } catch (err) {
      console.error("[Premium][Desktop] checkout error", err);
      setCheckoutLoading(false);
    }
  };

  const hasActive = subscriptions.length > 0;

  const heroStats = useMemo(
    () => [
      {
        value: translate("PremiumStat1Value"),
        label: translate("PremiumStat1Label"),
      },
      {
        value: translate("PremiumStat2Value"),
        label: translate("PremiumStat2Label"),
      },
      {
        value: translate("PremiumStat3Value"),
        label: translate("PremiumStat3Label"),
      },
    ],
    [translate]
  );

  const featureCards = useMemo(
    () => [
      {
        title: translate("PremiumFeature1Title"),
        description: translate("PremiumFeature1Desc"),
      },
      {
        title: translate("PremiumFeature4Title"),
        description: translate("PremiumFeature4Desc"),
      },
    ],
    [translate]
  );

  const planItems = useMemo(
    () =>
      [
        translate("PremiumPlanIncluded1"),
        translate("PremiumPlanIncluded2"),
        translate("PremiumPlanIncluded3"),
        translate("PremiumPlanIncluded4"),
        translate("PremiumPlanIncluded5"),
        translate("PremiumPlanIncluded6"),
      ].filter(Boolean),
    [translate]
  );

  const workflowSteps = useMemo(
    () => [
      {
        title: translate("PremiumWorkflow1Title"),
        description: translate("PremiumWorkflow1Desc"),
      },
      {
        title: translate("PremiumWorkflow2Title"),
        description: translate("PremiumWorkflow2Desc"),
      },
      {
        title: translate("PremiumWorkflow3Title"),
        description: translate("PremiumWorkflow3Desc"),
      },
    ],
    [translate]
  );

  const gradientBg =
    theme.palette.mode === "dark"
      ? "linear-gradient(135deg, #1a237e 0%, #0d47a1 40%, #1b5e20 100%)"
      : "linear-gradient(135deg, #eef2ff 0%, #e0f2ff 40%, #f1f8e9 100%)";

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        bgcolor: "background.default",
      }}
    >
      <LeftMenu />
      <Box sx={{ flexGrow: 1, ml: { md: 1 } }}>
        <HeaderBar
          title={translate("PremiumPageTitle")}
          subtitle={translate("PremiumPageSubtitle")}
          showProjectSelect={false}
          userName={currentUser?.displayName}
          userRole={role}
        />

        <Box sx={{ px: { xs: 2, md: 4 }, py: 4 }}>
          <Container maxWidth="lg" sx={{ px: { xs: 0, md: 2 } }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                p: { xs: 4, md: 6 },
                mb: 5,
                backgroundImage: gradientBg,
                color: theme.palette.mode === "dark" ? "#fff" : "inherit",
                border:
                  theme.palette.mode === "dark"
                    ? "1px solid rgba(255,255,255,0.12)"
                    : "1px solid rgba(15,23,42,0.08)",
              }}
            >
              <Stack spacing={3}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label={translate("PremiumBadge")}
                    color="primary"
                    variant={
                      theme.palette.mode === "dark" ? "filled" : "outlined"
                    }
                  />
                  <Typography variant="body2">
                    {translate("PremiumBadgeSubtitle")}
                  </Typography>
                </Stack>

                <Typography variant="h3" fontWeight={700}>
                  {translate("PremiumHeroTitle")}
                </Typography>
                <Typography
                  variant="h6"
                  color={
                    theme.palette.mode === "dark" ? "inherit" : "text.secondary"
                  }
                >
                  {translate("PremiumHeroSubtitle")}
                </Typography>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Button
                    variant="contained"
                    color="primary"
                    disableElevation
                    size="large"
                    onClick={() => !hasActive && handleSubscribe()}
                    disabled={checkoutLoading || subsLoading || hasActive}
                    sx={{ minWidth: 220, textTransform: "none" }}
                  >
                    {hasActive
                      ? translate("AlreadyPremium")
                      : translate("PremiumHeroCTA")}
                  </Button>
                </Stack>

                <Grid container spacing={3} sx={{ pt: 2 }}>
                  {heroStats.map((stat, index) => {
                    const Icon = heroIcons[index] || AutoAwesomeIcon;
                    return (
                      <Grid key={stat.label} item xs={12} md={4}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: 3,
                              backgroundColor:
                                theme.palette.mode === "dark"
                                  ? "rgba(255,255,255,0.12)"
                                  : "rgba(15,23,42,0.08)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Icon />
                          </Box>
                          <Box>
                            <Typography variant="h5" fontWeight={700}>
                              {stat.value}
                            </Typography>
                            <Typography variant="body2">
                              {stat.label}
                            </Typography>
                          </Box>
                        </Stack>
                      </Grid>
                    );
                  })}
                </Grid>
              </Stack>
            </Paper>

            <Stack spacing={4}>
              <Box>
                <Typography variant="overline" color="text.secondary">
                  {translate("PremiumFeaturesEyebrow")}
                </Typography>
                <Typography variant="h4" fontWeight={600}>
                  {translate("PremiumFeaturesTitle")}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mt: 1 }}
                >
                  {translate("PremiumFeaturesSubtitle")}
                </Typography>
              </Box>

              <Grid container spacing={3}>
                {featureCards.map((feature, index) => {
                  const Icon = heroIcons[index] || AutoAwesomeIcon;
                  return (
                    <Grid key={feature.title} item xs={12} md={4}>
                      <Paper
                        sx={{
                          p: 3,
                          borderRadius: 3,
                          height: "100%",
                          border: "1px solid",
                          borderColor:
                            theme.palette.mode === "dark"
                              ? "rgba(255,255,255,0.12)"
                              : "rgba(15,23,42,0.08)",
                        }}
                      >
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            bgcolor:
                              theme.palette.mode === "dark"
                                ? "rgba(255,255,255,0.12)"
                                : "rgba(15,23,42,0.06)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 2,
                          }}
                        >
                          <Icon fontSize="small" />
                        </Box>
                        <Typography variant="h6" gutterBottom>
                          {feature.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {feature.description}
                        </Typography>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>

              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper
                    sx={{
                      p: 4,
                      borderRadius: 4,
                      border: "1px solid",
                      borderColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.12)"
                          : "rgba(15,23,42,0.08)",
                    }}
                  >
                    <Typography variant="h5" fontWeight={600} gutterBottom>
                      {translate("PremiumPlanCardTitle")}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      gutterBottom
                    >
                      {translate("PremiumPlanCardSubtitle")}
                    </Typography>
                    <Typography variant="h3" color="primary" sx={{ my: 2 }}>
                      {currencySymbol}
                      {formatPrice(premiumPrice)}
                      <Typography component="span" fontSize="1.1rem">
                        {translate("PremiumPlanPriceSuffix")}
                      </Typography>
                    </Typography>
                    <List dense>
                      {planItems.map((item) => (
                        <ListItem key={item} disableGutters sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleIcon color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                    </List>
                    <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                      <Button
                        variant="contained"
                        disableElevation
                        onClick={() => !hasActive && handleSubscribe()}
                        disabled={checkoutLoading || subsLoading || hasActive}
                        sx={{ textTransform: "none", flexGrow: 1 }}
                      >
                        {hasActive
                          ? translate("AlreadyPremium")
                          : translate("PremiumHeroCTA")}
                      </Button>
                      <Button
                        variant="outlined"
                        component="a"
                        href="https://mail.google.com/mail/?view=cm&fs=1&to=arnaud@5ideasproduction.com"
                        target="_blank"
                        rel="noreferrer"
                        sx={{ textTransform: "none" }}
                      >
                        {translate("ContactUs")}
                      </Button>
                    </Stack>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper
                    sx={{
                      p: 4,
                      borderRadius: 4,
                      height: "100%",
                      bgcolor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.04)"
                          : "rgba(15,23,42,0.02)",
                      border: "1px dashed",
                      borderColor:
                        theme.palette.mode === "dark"
                          ? "rgba(255,255,255,0.15)"
                          : "rgba(15,23,42,0.2)",
                    }}
                  >
                    <Typography variant="h5" fontWeight={600}>
                      {translate("PremiumWorkflowTitle")}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1, mb: 3 }}
                    >
                      {translate("PremiumWorkflowSubtitle")}
                    </Typography>
                    <Stack spacing={3}>
                      {workflowSteps.map((step, index) => (
                        <Box key={step.title}>
                          <Typography
                            variant="subtitle2"
                            color="primary"
                            fontWeight={600}
                          >
                            {String(index + 1).padStart(2, "0")}
                          </Typography>
                          <Typography variant="h6">{step.title}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {step.description}
                          </Typography>
                          {index < workflowSteps.length - 1 && (
                            <Divider sx={{ mt: 2 }} />
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>
            </Stack>
          </Container>
        </Box>
      </Box>
    </Box>
  );
}
